import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Slide,
    TextField,
    MenuItem,
    Grid,
    FormLabel
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { ShowAlert } from "../../Component/ShowAlert";
import ExchanageRateService from "../../Services/ExchnageRateService";
import { ExchangeRates } from "./Interface";
import { SlideUpTransition } from "../../Component/Reusables/Transitions";
import InputTextField from "../../Component/Reusables/InputTextField";
import { convertToNumber } from "../../Component/config";

interface UpdateCurrencyPairsProps {
    open: boolean;
    accessToken: string;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
    currencyPairData: ExchangeRates;
}

interface UpdateCurrencyPairsState {
    fromCurrency: string;
    toCurrency: string;
    exchangeRate: string;
    markupPercentage: string;
    platformFeePercentage: string;
    platformFee: string;
    accessToken: string;
    exchangeRateError: string;
    markupPercentageError: string;
    platformFeePercentageError: string;
    platformFeeError: string;
    loading: boolean;
    checkedFields: {
        isMarkupActive: boolean,
        isPlatformPercentActive: boolean,
        isPlatformFeeActive: boolean,
    },
}

// Main class component
class UpdateCurrencyPairs extends Component<UpdateCurrencyPairsProps, UpdateCurrencyPairsState> {
    constructor(props: UpdateCurrencyPairsProps) {
        super(props);

        const { currencyPairData } = props;

        this.state = {
            fromCurrency: currencyPairData.fromCurrency,
            toCurrency: currencyPairData.toCurrency,
            exchangeRate: currencyPairData.exchangeRate || "",
            markupPercentage: currencyPairData.markupPercentage?.toString() || "",
            platformFeePercentage: currencyPairData.platformFeePercentage?.toString() || "",
            platformFee: currencyPairData.platformFee?.toString() || "",
            accessToken: props.accessToken,
            exchangeRateError: "",
            markupPercentageError: "",
            platformFeePercentageError: "",
            platformFeeError: "",
            loading: false,
            checkedFields: {
                isMarkupActive: currencyPairData.isMarkupActive,
                isPlatformPercentActive: currencyPairData.isPlatformFeePercentActive,
                isPlatformFeeActive: currencyPairData.isPlatformFeeActive,
            },
        };
    }

    async componentDidMount() {
        const accesstoken_ = await loadFromLocalStorage('accessToken');
        if (accesstoken_) {
            this.setState({ accessToken: accesstoken_.toString() });
        } else {
            localStorage.clear();
            window.location.href = '/';
        }
    }

    handleChange = (field: keyof UpdateCurrencyPairsState, value: string) => {
        // Clear any existing error when field changes
        this.setState((prevState) => ({
            ...prevState,
            [`${field}Error`]: ""
        }));

        // Validate numeric fields
        if (['exchangeRate', 'markupPercentage', 'platformFeePercentage', 'platformFee'].includes(field)) {
            const sanitizedValue = value.replace(/[^0-9.]/g, "");
            const decimalCount = (sanitizedValue.match(/\./g) || []).length;

            if (decimalCount > 1) {
                return; // Don't allow multiple decimal points
            }

            this.setState((prevState) => ({
                ...prevState,
                [field]: sanitizedValue
            }));
        } else {
            this.setState((prevState) => ({
                ...prevState,
                [field]: value
            }));
        }
    };

    validateFields = () => {
        let isValid = true;
        const errors = {
            exchangeRateError: "",
            markupPercentageError: "",
            platformFeePercentageError: "",
            platformFeeError: ""
        };

        // Exchange Rate validation (required)
        if (!this.state.exchangeRate || isNaN(parseFloat(this.state.exchangeRate)) || parseFloat(this.state.exchangeRate) <= 0) {
            errors.exchangeRateError = "Valid exchange rate is required";
            isValid = false;
        }

        if (
            this.state.checkedFields.isMarkupActive &&
            (
                !this.state.markupPercentage ||
                isNaN(parseFloat(this.state.markupPercentage)) ||
                convertToNumber(this.state.markupPercentage) <= 0
            )
        ) {
            errors.markupPercentageError = "Markup percentage must be greater than zero";
            isValid = false;
        }

        // Platform Fee Percentage validation (required)
        if (
            this.state.checkedFields.isPlatformPercentActive &&
            (
                !this.state.platformFeePercentage ||
                isNaN(parseFloat(this.state.platformFeePercentage)) ||
                convertToNumber(this.state.platformFeePercentage) < 0
            )
        ) {
            errors.platformFeePercentageError = "Platform fee percentage is required";
            isValid = false;
        }

        // Platform Fee validation (required)
        if (
            this.state.checkedFields.isPlatformFeeActive &&
            (
                !this.state.platformFee ||
                isNaN(parseFloat(this.state.platformFee)) ||
                convertToNumber(this.state.platformFee) < 0
            )
        ) {
            errors.platformFeeError = "Platform fee is required";
            isValid = false;
        }

        this.setState(errors);
        return isValid;
    };

    private handleDialogAction = async (action: string) => {
        if (action === "ok") {
            if (!this.validateFields()) {
                return;
            }

            const postData = {
                currencyId: this.props.currencyPairData.currencyId,
                fromCurrency: this.state.fromCurrency,
                toCurrency: this.state.toCurrency,
                exchangeRate: convertToNumber(this.state.exchangeRate),
                markupPercentage: convertToNumber(this.state.markupPercentage),
                platformFeePercentage: convertToNumber(this.state.platformFeePercentage),
                platformFee: convertToNumber(this.state.platformFee),
                isMarkupActive: this.state.checkedFields.isMarkupActive ? 1 : 0,
                isPlatformPercentActive: this.state.checkedFields.isPlatformPercentActive ? 1 : 0,
                isPlatformFeeActive: this.state.checkedFields.isPlatformFeeActive ? 1 : 0
            };

            this.setState({ loading: true });

            try {
                const response = await ExchanageRateService.updateCurrencyPairRates(
                    this.state.accessToken,
                    postData
                );

                if (response.ok) {
                    this.setState({ loading: false });
                    this.props.onClose?.(true, action);
                    ShowAlert("Success", "Currency pair rates updated successfully.", "success")
                        .then(() => { this.props.refreshData() });
                }
            } catch (error) {
                console.error("Error updating currency pair rates:", error);
                ShowAlert("Error", "Failed to update currency pair rates.", "error");
            }
        } else {
            this.props.onClose?.(false, action);
        }
    };

    handleCheckboxChange = (field: string, checked: boolean) => {
        this.setState((prevState: Readonly<any>) => {
            const updatedState: any = {
                checkedFields: {
                    ...prevState.checkedFields,
                    [field]: checked,
                },
            };

            const errors = {
                exchangeRateError: "",
                markupPercentageError: "",
                platformFeePercentageError: "",
                platformFeeError: ""
            };

            if (field === 'isMarkupActive' && !checked) {
                updatedState.markupPercentage = "";
                errors.markupPercentageError = "";
            }

            if (field === 'isPlatformPercentActive' && !checked) {
                updatedState.platformFeePercentage = "";
                errors.platformFeePercentageError = "";
            }

            if (field === 'isPlatformFeeActive' && !checked) {
                updatedState.platformFee = "";
                errors.platformFeeError = "";
            }
            this.setState(errors);
            return updatedState;
        });
    };

    render() {
        const { open, onClose, currencyPairData } = this.props;
        const {
            exchangeRate,
            markupPercentage,
            platformFeePercentage,
            platformFee,
            exchangeRateError,
            markupPercentageError,
            platformFeePercentageError,
            platformFeeError,
            loading,
            checkedFields
        } = this.state;

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionComponent={SlideUpTransition} keepMounted>
                <DialogTitle>Edit Currency Pair Rates</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                        <Grid container spacing={2}>
                            {/* From Currency (read-only) */}
                            <Grid item xs={12} sm={6}>
                                <FormLabel className="input-field-label">From currency</FormLabel>
                                <TextField
                                    fullWidth
                                    value={`${currencyPairData.senderCountry} (${currencyPairData.senderCurrency})`}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>

                            {/* To Currency (read-only) */}
                            <Grid item xs={12} sm={6}>
                                <FormLabel className="input-field-label">From currency</FormLabel>
                                <TextField
                                    fullWidth
                                    value={`${currencyPairData.receiverCountry} (${currencyPairData.receiverCurrency})`}
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </Grid>

                            {/* Exchange Rate */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Exchange Rate"
                                    placeholder="0.00"
                                    value={exchangeRate}
                                    startAdornment={currencyPairData.receiverCurrencySymbol}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('exchangeRate', value)}
                                    error={!!exchangeRateError}
                                    helperText={exchangeRateError}

                                />
                            </Grid>

                            {/* Markup Percentage */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Markup Percentage"
                                    placeholder="0.00"
                                    value={markupPercentage}
                                    endAdornment={"%"}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('markupPercentage', value)}
                                    error={!!markupPercentageError}
                                    helperText={markupPercentageError}
                                    checkedValue={checkedFields.isMarkupActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isMarkupActive", checked)}
                                    disabled={!checkedFields.isMarkupActive}
                                />
                            </Grid>

                            {/* Platform Fee Percentage */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Platform Fee %"
                                    placeholder="0.00"
                                    value={platformFeePercentage}
                                    endAdornment={"%"}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('platformFeePercentage', value)}
                                    error={!!platformFeePercentageError}
                                    helperText={platformFeePercentageError}
                                    checkedValue={checkedFields.isPlatformPercentActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isPlatformPercentActive", checked)}
                                    disabled={!checkedFields.isPlatformPercentActive}
                                />
                            </Grid>

                            {/* Platform Fee */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Platform Fee"
                                    placeholder="0.00"
                                    value={platformFee}
                                    startAdornment={"$"}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('platformFee', value)}
                                    error={!!platformFeeError}
                                    helperText={platformFeeError}
                                    checkedValue={checkedFields.isPlatformFeeActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isPlatformFeeActive", checked)}
                                    disabled={!checkedFields.isPlatformFeeActive}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => this.handleDialogAction("cancel")}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => this.handleDialogAction("ok")}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default UpdateCurrencyPairs;